import type { CSSProperties } from 'react';
import type { whyItWorksSection } from '../../../../app/_shared/marketing/landingContent';
import { withEmphasis } from './emphasisText';
import './problemAndWhy.css';

type Item = (typeof whyItWorksSection.items)[number];
type Inputs = typeof whyItWorksSection.inputs;

type Props = {
  eyebrow: string;
  h2: string;
  lead: string;
  items: readonly Item[];
  inputs: Inputs;
  closer: string;
};

/**
 * The mechanism beat: each row states what Kynward does, then the failure that move exists to
 * remove. The reason sentences carry their own leading "Because" in the copy rather than having
 * one prepended here — the strings stay whole sentences, readable wherever else they get used.
 *
 * The inputs block under the rows names what matching reads and what it never reads. It is a
 * disclosure, not a feature pitch, so it uses plain lists rather than cards.
 */
export function WhyItWorksSection({ eyebrow, h2, lead, items, inputs, closer }: Props) {
  return (
    <section className="whyworks-section" aria-labelledby="whyworks-heading">
      <header className="whyworks-section__head">
        <p className="ml-text-eyebrow whyworks-section__eyebrow">{eyebrow}</p>
        <h2
          id="whyworks-heading"
          className="ml-heading-serif ml-text-heading whyworks-section__title">
          {h2}
        </h2>
        <p className="whyworks-section__lead">{lead}</p>
      </header>

      <ol className="whyworks-list">
        {items.map((item, index) => (
          <li
            key={item.id}
            className="whyworks-row ml-revealStaggerItem"
            style={{ '--reveal-i': index } as CSSProperties}>
            <span className="whyworks-row__rule" aria-hidden />
            <div className="whyworks-row__copy">
              <p className="ml-heading-sans whyworks-row__mechanic">{item.mechanic}</p>
              <p className="whyworks-row__because">{withEmphasis(item.because)}</p>
            </div>
          </li>
        ))}
      </ol>

      <section className="whyworks-inputs" aria-labelledby="whyworks-inputs-heading">
        <h3 id="whyworks-inputs-heading" className="ml-heading-serif whyworks-inputs__title">
          {inputs.title}
        </h3>
        <div className="whyworks-inputs__cols">
          <div>
            <p className="ml-text-eyebrow whyworks-inputs__label">{inputs.goesInLabel}</p>
            <ul className="whyworks-inputs__list">
              {inputs.goesIn.map((entry) => (
                <li key={entry.id} className="whyworks-inputs__in">
                  <span className="whyworks-inputs__inLabel">{entry.label}</span>
                  <span className="whyworks-inputs__inDetail">{entry.detail}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="ml-text-eyebrow whyworks-inputs__label">{inputs.neverInLabel}</p>
            <ul className="whyworks-inputs__list">
              {inputs.neverIn.map((line) => (
                <li key={line} className="whyworks-inputs__never">
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <p className="whyworks-inputs__promise">{withEmphasis(inputs.promise)}</p>
      </section>

      <p className="whyworks-section__closer">{withEmphasis(closer)}</p>
    </section>
  );
}
